/**
 * @fileoverview Full-screen dark layout wrapper for onboarding wizard.
 * @module features/onboarding/components/OnboardingLayout
 */

import { ReactElement, ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { EllipsisVertical, LogOut } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { authService } from '@/lib/authService'
import { StepIndicator } from './StepIndicator'

interface OnboardingLayoutProps {
  children: ReactNode
  /** Current step index (0-based) for visible steps */
  currentStep: number
  /** Total number of visible steps */
  totalSteps: number
  /** Array of completed step indices for visible steps */
  completedSteps: number[]
  /** Whether to show the stepper */
  showStepper?: boolean
  /** Callback when a step dot is clicked */
  onStepClick?: (stepIndex: number) => void
  /** Additional CSS classes */
  className?: string
}

/**
 * Full-screen dark layout for the onboarding wizard.
 * Provides centered content area with progress indicator at bottom.
 *
 * @component
 * @example
 * ```tsx
 * <OnboardingLayout currentStep={0} totalSteps={8} completedSteps={[]}>
 *   <WelcomeStep onContinue={handleNext} />
 * </OnboardingLayout>
 * ```
 */
export function OnboardingLayout({
  children,
  currentStep,
  totalSteps,
  completedSteps,
  showStepper = true,
  onStepClick,
  className,
}: OnboardingLayoutProps): ReactElement {
  const navigate = useNavigate()
  const { t } = useTranslation('common')

  return (
    <div
      className={cn(
        'min-h-svh w-full bg-background flex flex-col',
        className
      )}
    >
      {/* Top-right kebab menu */}
      <div className="absolute top-4 right-4 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="text-muted-foreground">
              <EllipsisVertical className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                authService.logout()
                navigate('/', { replace: true })
              }}
            >
              <LogOut className="size-4" />
              {t('userMenu.logOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Main content area - centered */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{
              opacity: 0,
              filter: 'blur(12px)',
              scale: 0.95,
              y: 20,
            }}
            animate={{
              opacity: 1,
              filter: 'blur(0px)',
              scale: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              filter: 'blur(8px)',
              scale: 0.98,
              y: -10,
            }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="w-full"
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress indicator - fixed at bottom, hidden during create-org */}
      {showStepper && (
        <div className="pb-8 flex justify-center">
          <StepIndicator
            currentStep={currentStep}
            totalSteps={totalSteps}
            completedSteps={completedSteps}
            onStepClick={onStepClick}
          />
        </div>
      )}
    </div>
  )
}
